import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { DIALOG_CLOSE_BUTTON_SX } from "../../constants/dialogCloseButtonStyles";
import { ELEVATED_DIALOG_Z_INDEX_OFFSET } from "../../constants/dialogZIndex";
import { getManualAssessmentProcessDefinition } from "../../services/manualAssessmentService";
import { ProcessBpmnViewer } from "./ProcessBpmnViewer";

const BPMN_VIEWER_HEIGHT = "480px";

export type ProcessBpmnDialogTarget = Readonly<{
  processId: number;
  name: string;
}>;

export type ProcessBpmnDialogProps = Readonly<{
  target: ProcessBpmnDialogTarget | null;
  onClose: () => void;
}>;

export default function ProcessBpmnDialog({
  target,
  onClose,
}: ProcessBpmnDialogProps) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [definitionXml, setDefinitionXml] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState<string | null>(null);
  /** Mount bpmn-js only after the dialog enter transition so fit-viewport has real dimensions. */
  const [dialogEntered, setDialogEntered] = useState(false);

  useEffect(() => {
    if (target == null) {
      setDialogEntered(false);
      setLoading(false);
      setError(null);
      setDefinitionXml(null);
      setTemplateName(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setDefinitionXml(null);
    setTemplateName(null);

    void (async () => {
      try {
        const definition = await getManualAssessmentProcessDefinition(
          target.processId,
        );
        if (cancelled) return;
        if (!definition?.definitionXml) {
          setError("Process diagram is not available.");
          return;
        }
        setDefinitionXml(definition.definitionXml);
        setTemplateName(definition.processTemplateName);
      } catch {
        if (cancelled) return;
        setError("Failed to load process diagram. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [target?.processId]);

  const title = templateName?.trim() || target?.name || "Process";

  return (
    <Dialog
      open={target != null}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            zIndex: (theme) =>
              theme.zIndex.modal + ELEVATED_DIALOG_Z_INDEX_OFFSET + 100,
          },
        },
        transition: {
          onEntered: () => setDialogEntered(true),
          onExited: () => setDialogEntered(false),
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
          fontWeight: 600,
        }}
      >
        {title}
        <IconButton
          aria-label="Close process diagram"
          onClick={onClose}
          sx={DIALOG_CLOSE_BUTTON_SX}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {loading ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: BPMN_VIEWER_HEIGHT,
            }}
          >
            <CircularProgress size={32} />
          </Box>
        ) : null}
        {!loading && error ? (
          <Typography variant="body2" color="error" sx={{ py: 2 }}>
            {error}
          </Typography>
        ) : null}
        {!loading && !error && definitionXml && dialogEntered ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: "100%",
              minHeight: BPMN_VIEWER_HEIGHT,
            }}
          >
            <ProcessBpmnViewer
              key={target?.processId ?? "bpmn"}
              bpmnXML={definitionXml}
              height={BPMN_VIEWER_HEIGHT}
            />
          </Box>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
